import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, HardDrive, Cpu, Monitor, Database } from 'lucide-react';
import { VPS_TYPES } from '@/data/vps';
import { useTranslation } from '@/lib/i18n';
import { SEOHead } from '@/components/SEOHead';
import { ReviewForestWidget } from '@/components/ReviewForestWidget';

const typeIcons = {
  storage:    Database,
  'kvm-ryzen': Cpu,
  kvm:        HardDrive,
  windows:    Monitor,
};

const typeColors = {
  storage:    { color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  'kvm-ryzen': { color: 'text-primary',    bg: 'bg-primary/10' },
  kvm:        { color: 'text-blue-400',   bg: 'bg-blue-500/10' },
  windows:    { color: 'text-sky-400',    bg: 'bg-sky-500/10' },
};

export default function VPSPage() {
  const { t } = useTranslation();

  const benefits = t('vps.benefits').split('|');

  return (
    <Layout>
      <SEOHead
        title="VPS Hosting – KVM, Ryzen, Storage & Windows"
        description="Virtual servers from €1.00/month. KVM Ryzen VPS, Storage VPS, Windows VPS – all with full root access, DDoS protection and no contracts."
        canonical="/vps"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'VPS Server Plans',
          description: 'Virtual private servers at DeinServerHost',
          url: 'https://deinserverhost.com/vps',
          numberOfItems: 4,
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Storage VPS', url: 'https://deinserverhost.com/vps/storage' },
            { '@type': 'ListItem', position: 2, name: 'KVM Ryzen VPS', url: 'https://deinserverhost.com/vps/kvm-ryzen' },
            { '@type': 'ListItem', position: 3, name: 'KVM VPS', url: 'https://deinserverhost.com/vps/kvm' },
            { '@type': 'ListItem', position: 4, name: 'Windows VPS', url: 'https://deinserverhost.com/vps/windows' },
          ],
        }}
      />
      <div className="section-spacing">
        <div className="container-main">

          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-14"
          >
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              Virtual <span className="text-gradient">Private Server</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              {t('vps.subtitle')}
            </p>
            {/* Benefits pills */}
            <div className="flex flex-wrap justify-center gap-3">
              {benefits.map((b) => (
                <div key={b} className="flex items-center gap-2 px-4 py-2 rounded-full glass border border-border text-sm">
                  <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                  {b}
                </div>
              ))}
            </div>
          </motion.div>

          {/* VPS type cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {VPS_TYPES.map((vps, index) => {
              const Icon = typeIcons[vps.slug] || HardDrive;
              const { color, bg } = typeColors[vps.slug] || typeColors.kvm;
              return (
                <motion.div
                  key={vps.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.07 }}
                  data-testid={`vps-type-${vps.slug}`}
                >
                  <Link to={`/vps/${vps.slug}`} className="group block h-full">
                    <div className="glass rounded-2xl border border-border hover:border-primary/50 transition-all duration-300 p-6 h-full flex flex-col">
                      <div className={`w-12 h-12 rounded-xl ${bg} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                        <Icon className={`w-6 h-6 ${color}`} />
                      </div>

                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h2 className="text-lg font-black group-hover:text-primary transition-colors leading-tight">
                          {t(`vps.${vps.tPrefix}_title`)}
                        </h2>
                        {vps.badgeKey && (
                          <Badge className="bg-primary text-xs shrink-0">{t(`vps.${vps.badgeKey}`)}</Badge>
                        )}
                      </div>

                      <p className="text-xs text-muted-foreground mb-5 leading-relaxed flex-1">
                        {t(`vps.${vps.tPrefix}_tagline`)}
                      </p>

                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-xs text-muted-foreground">{t('vps.from')} </span>
                          <span className="text-2xl font-black text-primary">€{vps.fromPrice}</span>
                          <span className="text-xs text-muted-foreground">{t('vps.perMonth')}</span>
                        </div>
                        <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {/* Reviews */}
          <ReviewForestWidget className="mb-12" />

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="text-center p-8 glass rounded-2xl"
          >
            <p className="text-muted-foreground text-sm mb-4">{t('vps.notSure')}</p>
            <Link to="/product-finder">
              <Button className="bg-primary hover:bg-primary/90 glow-hover">
                {t('vps.useProductFinder')}
              </Button>
            </Link>
          </motion.div>

        </div>
      </div>
    </Layout>
  );
}
